/**
 * Avatar Column Pattern
 * 
 * Creates avatar columns with image fallbacks and initials.
 */

import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { TABLE_SETTINGS } from '../../constants/tableSettings';
import type { AvatarColumnOptions } from './types';

/**
 * Creates an avatar column with image and initials fallback
 * 
 * @param accessor - The data accessor key for avatar URL or user object
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Basic usage - accessor points to avatar URL
 * avatarColumn('avatar')
 * 
 * // With name accessor for initials fallback
 * avatarColumn('avatar', {
 *   nameAccessor: 'name',
 *   size: 'md'
 * })
 */
export function avatarColumn<T>(
  accessor: keyof T,
  options: AvatarColumnOptions = {}
): ColumnDef<T> {
  const {
    header = '',
    sortable = false,
    resizable = false,
    fallbackType = TABLE_SETTINGS.avatar.fallbackType,
    nameAccessor,
    width = 60,
    maxWidth = 80,
    minWidth = 40,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    cell: ({ row }) => {
      const avatarUrl = row.original[accessor] as string;
      const nameValue = nameAccessor ? row.original[nameAccessor as keyof T] as string : null;
      
      // Generate initials if name is provided
      let initials = '';
      if (nameValue && fallbackType === 'initials') {
        const nameParts = nameValue.split(' ').filter(Boolean);
        initials = nameParts.length >= 2
          ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
          : nameParts[0]?.charAt(0).toUpperCase() || '?';
      }
      
      // Return proper React element for avatar with image/initials fallback
      return React.createElement('div', {
        className: 'flex items-center justify-center'
      }, avatarUrl 
        ? React.createElement('img', {
            src: avatarUrl,
            alt: nameValue || 'User avatar',
            className: 'w-8 h-8 rounded-full'
          })
        : React.createElement('div', {
            className: 'w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center text-sm font-medium'
          }, initials || '??')
      );
    },
  };
}

/**
 * Creates a simple avatar column with initials fallback
 */
export function simpleAvatarColumn<T>(
  avatarAccessor: keyof T, 
  nameAccessor: keyof T,
  header?: string
): ColumnDef<T> {
  return avatarColumn(avatarAccessor, {
    header,
    nameAccessor: nameAccessor as string,
    fallbackType: 'initials',
    size: 'sm',
  });
}

/**
 * User object interface for avatar display
 */
interface UserAvatarData {
  name?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  avatarUrl?: string;
}

/**
 * Creates a user avatar column (assumes user object with avatar and name)
 */
export function userAvatarColumn<T>(
  userAccessor: keyof T,
  header?: string
): ColumnDef<T> {
  return {
    id: String(userAccessor),
    header: header || '',
    enableSorting: false,
    enableResizing: false,
    size: 60,
    cell: ({ row }) => {
      const user = row.original[userAccessor] as UserAvatarData | null;
      
      if (!user) {
        return React.createElement('div', {
          className: 'flex items-center justify-center'
        }, React.createElement('div', {
          className: 'w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center text-sm font-medium'
        }, '?'));
      }
      
      // Handle both user.name and user.firstName/lastName patterns
      const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
      const nameParts = name.split(' ').filter(Boolean);
      const initials = nameParts.length >= 2
        ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
        : nameParts[0]?.charAt(0).toUpperCase() || '?';
      
      return React.createElement('div', {
        className: 'flex items-center justify-center'
      }, user.avatar || user.avatarUrl 
        ? React.createElement('img', {
            src: user.avatar || user.avatarUrl,
            alt: name || 'User avatar',
            className: 'w-8 h-8 rounded-full'
          })
        : React.createElement('div', {
            className: 'w-8 h-8 rounded-full bg-primary text-primary-content flex items-center justify-center text-sm font-medium'
          }, initials)
      );
    },
  };
} 